import { useState, useCallback } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { X, Plus, Loader2, HardHat } from "lucide-react";

interface CreateSubDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}

export function CreateSubDialog({ open, onClose, onCreated }: CreateSubDialogProps) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    email: "",
    phone: "",
    specialties: "",
    default_rate: 0,
    rate_type: "hourly",
  });

  const handleCreate = useCallback(async () => {
    if (!form.first_name.trim() && !form.company_name.trim()) {
      notify("Name or company is required", { type: "warning" });
      return;
    }
    setCreating(true);
    try {
      const specs = form.specialties
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const result = await create(
        "subcontractors",
        {
          data: {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            company_name: form.company_name.trim(),
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            specialties: specs,
            default_rate: form.default_rate || null,
            rate_type: form.rate_type,
          },
        },
        { returnPromise: true }
      );

      notify("Subcontractor added", { type: "success" });
      refresh();
      const newId = (result as { data?: { id?: string } })?.data?.id;
      if (newId && onCreated) onCreated(newId);
      onClose();
      setForm({
        first_name: "", last_name: "", company_name: "",
        email: "", phone: "", specialties: "",
        default_rate: 0, rate_type: "hourly",
      });
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setCreating(false);
    }
  }, [form, create, notify, refresh, onCreated, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <HardHat className="w-4 h-4" />
              Add Subcontractor
            </h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">First name</label>
                <input type="text" value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Last name</label>
                <input type="text" value={form.last_name}
                  onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Company</label>
              <input type="text" value={form.company_name}
                onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Phone</label>
                <input type="text" value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Email</label>
                <input type="text" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Specialties (comma-separated)
              </label>
              <input type="text" value={form.specialties}
                onChange={(e) => setForm((f) => ({ ...f, specialties: e.target.value }))}
                placeholder="plumbing, electrical, framing"
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Default rate ($)</label>
                <input type="number" min={0} step={0.5} value={form.default_rate}
                  onChange={(e) => setForm((f) => ({ ...f, default_rate: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded text-sm text-right" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Rate type</label>
                <select value={form.rate_type}
                  onChange={(e) => setForm((f) => ({ ...f, rate_type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm">
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="fixed">Fixed</option>
                  <option value="per_job">Per Job</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button onClick={onClose} disabled={creating}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={handleCreate}
              disabled={creating || (!form.first_name.trim() && !form.company_name.trim())}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
